import { isNil, isObject, get, set, cloneDeep, sortBy } from 'lodash';

export const MAX_SERIALIZED_SIZE = 5000;
const PROGRESSIVE_ARRAY_LIMITS = [25, 10, 1];

const buildArrayMap = obj => {
    const arrayMap = new Map();

    const scanRecursive = (value, path = '') => {
        if (isNil(value)) {
            return;
        }

        if (Array.isArray(value) && value.length > 0) {
            arrayMap.set(path, value.length);
            value.forEach((item, index) => {
                scanRecursive(item, `${path}[${index}]`);
            });
        } else if (isObject(value)) {
            for (const [key, val] of Object.entries(value)) {
                const currentPath = path ? `${path}.${key}` : key;
                scanRecursive(val, currentPath);
            }
        }
    };

    scanRecursive(obj);
    return arrayMap;
};

const sortArrayPaths = arrayMap => {
    return sortBy(Array.from(arrayMap.keys()), [
        path => -(path.match(/\./g) || []).length,
        path => -arrayMap.get(path),
    ]);
};

const truncateArrayAtPath = (obj, path, maxLength) => {
    const currentArray = get(obj, path);

    if (Array.isArray(currentArray) && currentArray.length > maxLength) {
        set(obj, path, currentArray.slice(0, maxLength));
        return true;
    }

    return false;
};

const nullReplacer = (key, val) => {
    if (!isNil(val)) return val;
};

const serializedField = value => {
    if (isNil(value) || typeof value === 'string') {
        return value;
    }

    try {
        const initialSerialization = JSON.stringify(value, nullReplacer);

        if (initialSerialization.length <= MAX_SERIALIZED_SIZE) {
            return initialSerialization;
        }

        let workingObj = cloneDeep(value);
        const truncatedFieldsObj = {};

        const arrayMap = buildArrayMap(workingObj);
        const sortedPaths = sortArrayPaths(arrayMap);

        for (const maxLength of PROGRESSIVE_ARRAY_LIMITS) {
            for (const path of sortedPaths) {
                const originalSize = arrayMap.get(path);

                if (originalSize <= maxLength) {
                    continue;
                }

                const didTruncate = truncateArrayAtPath(workingObj, path, maxLength);

                if (didTruncate) {
                    truncatedFieldsObj[path] = true;

                    const objToSerialize = {
                        ...workingObj,
                        truncated: true,
                        truncatedFields: Object.keys(truncatedFieldsObj),
                    };

                    const testSerialization = JSON.stringify(objToSerialize, nullReplacer);

                    if (testSerialization.length <= MAX_SERIALIZED_SIZE) {
                        return testSerialization;
                    }
                }
            }
        }

        // In case we truncate, but it still wasn't enough to be under the limit
        const finalObj =
            Object.keys(truncatedFieldsObj).length > 0
                ? {
                      ...workingObj,
                      truncated: true,
                      truncatedFields: Object.keys(truncatedFieldsObj),
                  }
                : workingObj;

        return JSON.stringify(finalObj, nullReplacer);
    } catch (e) {
        return e.message;
    }
};

export const serializeAppEvent = event => {
    const serializedEvent = { ...event };

    serializedEvent.value = serializedField(event.value);
    serializedEvent.prevValue = serializedField(event.prevValue);

    if (typeof event.data === 'string') {
        serializedEvent.data = { rawData: JSON.stringify({ stringContent: event.data }) };
    } else if (isNil(event.data)) {
        serializedEvent.data = { rawData: JSON.stringify({}) };
    } else {
        const rawDataResult = serializedField(event.data);
        serializedEvent.data = {
            rawData: rawDataResult,
            ...event.data,
        };
    }

    return serializedEvent;
};
